import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Select,
  Input,
  Button,
  Text,
  VStack,
  useToast,
  Flex,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { getData, postData } from '../../../utils/httpClient';
import {
  GRADE_MIN,
  GRADE_MAX,
  GRADE_RESULTS,
  inferResultFromGrade,
  isValidGrade,
} from '../../../utils/gradeScale';

// ACTA de un EXAMEN individual (Módulo 7).
// Requerimiento cliente:
//   - Registrar la NOTA del estudiante en el examen.
//   - Escala centralizada en utils/gradeScale.js (Módulo 9).
//
// Endpoints:
//   - GET  /exam/inscriptions/schedule/:examScheduleId -> lista de inscriptos.
//   - POST /exam/grades -> registrar nota (CreateExamGradeDto).

export default function ExamActaView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await getData(`/exam/inscriptions/schedule/${id}`);
        const rows = res?.payload?.inscriptions || [];
        setStudents(
          rows.map((r) => ({
            studentId: r.student_id ?? r.studentId,
            inscriptionId: r.id,
            name: r.student_name || '',
            lastname: r.student_last_name || '',
            grade: '',
            result: '',
          })),
        );
      } catch (error) {
        setStudents([]);
        toast({
          title: 'Error al obtener inscriptos al examen',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, toast]);

  const handleGradeChange = (studentId, grade) => {
    setStudents((prev) =>
      prev.map((s) =>
        s.studentId === studentId
          ? { ...s, grade, result: inferResultFromGrade(grade) }
          : s,
      ),
    );
  };

  const handleResultChange = (studentId, result) => {
    setStudents((prev) =>
      prev.map((s) => (s.studentId === studentId ? { ...s, result } : s)),
    );
  };

  const handleSave = async () => {
    if (!students.length) return;
    setSaving(true);
    try {
      // Se guardan una a una porque POST /exam/grades recibe
      // CreateExamGradeDto por estudiante. Si se agrega un endpoint
      // batch en backend, sustituir por una sola llamada.
      await Promise.all(
        students.map((s) =>
          postData('/exam/grades', {
            examScheduleId: Number(id),
            studentId: s.studentId,
            grade: Number(s.grade),
            result: s.result || inferResultFromGrade(s.grade),
          }),
        ),
      );
      toast({
        title: 'Acta guardada',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error al guardar el acta',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const isValid = useMemo(
    () => students.length > 0 && students.every((s) => isValidGrade(s.grade)),
    [students],
  );

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="md">ACTA del examen #{id}</Heading>
        <Button variant="outline" onClick={() => navigate(-1)}>
          Volver
        </Button>
      </Flex>

      <Text color="gray.500" mb={2}>
        Escala de notas: 0-12. 0-3: No aprobado, 4-8: Aprobado, 9-12: Aprobado + Exonerado.
      </Text>

      <VStack align="stretch" spacing={4}>
        {loading && <Text color="gray.500">Cargando inscriptos...</Text>}
        {!loading && students.length === 0 && (
          <Text color="gray.500">No hay inscriptos a este examen.</Text>
        )}
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Estudiante</Th>
              <Th>Nota</Th>
              <Th>Resultado</Th>
            </Tr>
          </Thead>
          <Tbody>
            {students.map((s) => (
              <Tr key={s.studentId}>
                <Td>
                  {s.name} {s.lastname}
                </Td>
                <Td>
                  <Input
                    size="sm"
                    type="number"
                    min={GRADE_MIN}
                    max={GRADE_MAX}
                    value={s.grade}
                    onChange={(e) =>
                      handleGradeChange(s.studentId, e.target.value)
                    }
                    maxW="100px"
                  />
                </Td>
                <Td>
                  <Select
                    size="sm"
                    value={s.result}
                    onChange={(e) =>
                      handleResultChange(s.studentId, e.target.value)
                    }
                    maxW="180px"
                  >
                    <option value="">-</option>
                    <option value={GRADE_RESULTS.APROBADO}>Aprobado</option>
                    <option value={GRADE_RESULTS.NO_APROBADO}>No aprobado</option>
                    <option value={GRADE_RESULTS.EXONERADO}>Exonerado</option>
                  </Select>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        <Flex justify="flex-end">
          <Button
            colorScheme="blue"
            isDisabled={!isValid || saving}
            onClick={handleSave}
          >
            Guardar acta
          </Button>
        </Flex>
      </VStack>
    </Box>
  );
}
